<script lang="ts">
  import {
    orgSecuritySensitiveDataStore,
    ROOT,
    type DirectorySensitiveContentCount,
    type DocumentSensitiveContentCount,
    type SensitiveContentCount,
  } from "$lib/stores/orgSensitiveData";
  import { LoadingSpinner } from "$lib/ui/loading-spinner";
  import { AlignCenter, AlignJustify, ExternalLink, File, Folder, Home } from "lucide-svelte";
  import clsx from "clsx";
  import { categoryMeta, getCategoryMetaWithCustom } from "./categoryLabels";
  import { push, querystring, location } from "svelte-spa-router";
  import { onMount } from "svelte";

  import { infoTypes, getInfoTypesWithCustom } from "./infoTypes";
  import * as Select from "$ui/select";
  import CategoryChip from "./CategoryChip.svelte";
  import type { Selected } from "bits-ui";
  import { derived as storeDerived } from "svelte/store";
  import GoogleLogo from "$assets/icons/google-logo.svelte";
  import MicrosoftLogo from "$assets/icons/microsoft-logo.svelte";
  import { selectedOrgStore } from "$lib/stores/orgs";
  import { listCustomInfoTypes } from "$lib/services/customInfoTypes";
  import type { CustomInfoType } from "$lib/models/base";

  let detailedView = $state(false);
  let selectedCategories: Selected<string>[] = $state([]);
  let hasNoResults = $state(false);
  let showNonSensitive = $state(false);
  let customInfoTypes: CustomInfoType[] = $state([]);

  // Merged category metadata including custom infoTypes
  let mergedCategoryMeta = $derived(getCategoryMetaWithCustom(customInfoTypes));
  // Merged infoTypes mapping including custom infoTypes
  let mergedInfoTypes = $derived(getInfoTypesWithCustom(customInfoTypes));

  // Sorted category entries with custom types first
  let sortedCategoryEntries = $derived(() => {
    const entries = Object.entries(mergedCategoryMeta);
    // Sort: custom types first (alphabetically), then built-in types (alphabetically)
    return entries.sort((a, b) => {
      const aIsCustom = a[1].isCustom || false;
      const bIsCustom = b[1].isCustom || false;

      if (aIsCustom && !bIsCustom) return -1;
      if (!aIsCustom && bIsCustom) return 1;

      // Within the same group, sort alphabetically by label
      return a[1].label.localeCompare(b[1].label);
    });
  });

  const pluralRules = new Intl.PluralRules("en-US");

  function getDocumentText(count: number): string {
    const pluralForm = pluralRules.select(count);
    const word = pluralForm === "one" ? "document" : "documents";
    return word;
  }

  function getDirectoryText(count: number): string {
    const pluralForm = pluralRules.select(count);
    const word = pluralForm === "one" ? "directory" : "directories";
    return word;
  }

  onMount(async () => {
    // Load custom infoTypes for the organization
    if ($selectedOrgStore?.id) {
      try {
        customInfoTypes = await listCustomInfoTypes($selectedOrgStore.id);
      } catch (e) {
        console.error("Failed to load custom info types:", e);
      }
    }

    const search = new URLSearchParams($querystring);

    const encodedCategories = search
      .getAll("categories")
      .map((cat) => cat.split(","))
      .flat();
    if (encodedCategories.length > 0) {
      const categories = encodedCategories
        .map((cat) => decodeURIComponent(cat))
        .filter((cat) => mergedCategoryMeta.hasOwnProperty(cat))
        .map((cat) => ({ label: mergedCategoryMeta[cat].label, value: cat }));

      selectedCategories = categories;
    }
  });

  // Reload custom infoTypes when org changes
  $effect(() => {
    if ($selectedOrgStore?.id) {
      listCustomInfoTypes($selectedOrgStore.id)
        .then((types) => {
          customInfoTypes = types;
        })
        .catch((e) => {
          console.error("Failed to load custom info types:", e);
        });
    }
  });

  const currentPath = storeDerived([orgSecuritySensitiveDataStore, querystring], ([finding, querystring]): string[] => {
    if (!finding) {
      return [];
    }
    if (finding.path) {
      hasNoResults = false;
      const provider = finding.resourceId.split(":")[0];
      return [provider, ...finding.path?.split("/").filter(Boolean)];
    }
    // finding is empty
    hasNoResults = true;
    const search = new URLSearchParams(querystring);
    return search.get("path")?.split("/").filter(Boolean) ?? [];
  });

  function groupFindings(findings: SensitiveContentCount[]): [string, number][] {
    const grouped: Record<string, number> = {};

    findings.forEach((finding) => {
      const group = mergedInfoTypes[finding.sensitiveContentType];
      if (group && mergedCategoryMeta.hasOwnProperty(group)) {
        grouped[group] = (grouped[group] || 0) + finding.count;
      }
    });

    return Object.entries(grouped).sort((a, b) => b[1] - a[1]);
  }

  const sensitiveDirectories = $derived(
    $orgSecuritySensitiveDataStore?.subdirectoryChildren?.filter((node) => {
      const filteredFindings =
        node.sensitiveContentCount?.filter(
          (sensitiveContent) =>
            selectedCategories.length === 0 ||
            selectedCategories.some((sl) => sl.value === sensitiveContent.sensitiveContentType),
        ) ?? [];

      return groupFindings(filteredFindings).length > 0;
    }) ?? [],
  );

  const nonSensitiveDirectories = $derived(
    $orgSecuritySensitiveDataStore?.subdirectoryChildren?.filter((node) => {
      const filteredFindings =
        node.sensitiveContentCount?.filter(
          (sensitiveContent) =>
            selectedCategories.length === 0 ||
            selectedCategories.some((sl) => sl.value === sensitiveContent.sensitiveContentType),
        ) ?? [];

      return groupFindings(filteredFindings).length === 0;
    }) ?? [],
  );

  const nonSensitiveDocuments = $derived(
    $orgSecuritySensitiveDataStore?.documentChildren?.filter((doc) => {
      const filteredFindings =
        doc.sensitiveContentCount?.filter(
          (sensitiveContent) =>
            selectedCategories.length === 0 ||
            selectedCategories.some((sl) => sl.value === sensitiveContent.sensitiveContentType),
        ) ?? [];

      return groupFindings(filteredFindings).length === 0;
    }) ?? [],
  );

  const sensitivedocuments = $derived(
    $orgSecuritySensitiveDataStore?.documentChildren?.filter((doc) => {
      const filteredFindings =
        doc.sensitiveContentCount?.filter(
          (sensitiveContent) =>
            selectedCategories.length === 0 ||
            selectedCategories.some((sl) => sl.value === sensitiveContent.sensitiveContentType),
        ) ?? [];

      return groupFindings(filteredFindings).length > 0;
    }) ?? [],
  );

  const groupedDirectories = $derived(
    sensitiveDirectories.reduce(
      (acc, directory) => {
        const provider = directory.resourceId.split(":")[0];
        if (!acc[provider]) {
          acc[provider] = [];
        }
        acc[provider].push(directory);
        return acc;
      },
      {} as Record<string, DirectorySensitiveContentCount[]>,
    ),
  );
</script>

<div class="pb-6">
  <div class="flex justify-between items-center mb-5">
    <nav class="flex flex-wrap items-center text-sm text-gray-500" aria-label="Breadcrumb">
      <button
        onclick={() => {
          const params = new URLSearchParams($querystring);
          params.set("path", "/");
          push(`${$location}?${params.toString()}`);
        }}
        class="hover:text-blue-600 font-medium transition px-2 py-1 rounded"
      >
        <Home class="h-6 w-6 text-gray-400 hover:text-blue-600" />
      </button>
      {#each $currentPath.slice(1) as part, index}
        <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        <button
          onclick={() => {
            const params = new URLSearchParams($querystring);
            params.set("path", $currentPath?.slice(0, index + 2).join("/"));
            push(`${$location}?${params.toString()}`);
          }}
          class="hover:text-blue-600 font-medium transition px-2 py-1 rounded"
        >
          {part}
        </button>
      {/each}
    </nav>

    <div class="flex items-center gap-8">
      <Select.Root
        multiple
        selected={selectedCategories}
        onSelectedChange={(selected) => {
          if (selected) selectedCategories = selected;

          const searchParams = new URLSearchParams($querystring);
          searchParams.set("categories", selectedCategories.map((sc) => sc.value).join(","));

          push(`${$location}?${searchParams.toString()}`);
        }}
      >
        <div class="flex items-center gap-2">
          <span class="text-sm"> Filter by Category</span>
          <Select.Trigger name="category" class="w-[300px]">
            <Select.Value placeholder="Select Categories" />
          </Select.Trigger>
          <Select.Content class="h-[600px] overflow-y-scroll">
            {#if customInfoTypes.length > 0}
              <Select.Group>
                <Select.Label>Custom Types</Select.Label>
                {#each sortedCategoryEntries().filter(([_, meta]) => meta.isCustom) as [key, meta]}
                  <Select.Item
                    value={key}
                    label={meta.label}
                    isSelected={!!selectedCategories.find((cat) => cat.value === key)}
                  >
                    <CategoryChip label={meta.label} color={meta.color} />
                  </Select.Item>
                {/each}
              </Select.Group>
            {/if}
            <Select.Group>
              <Select.Label>Built-in Categories</Select.Label>
              {#each sortedCategoryEntries().filter(([_, meta]) => !meta.isCustom) as [key, meta]}
                <Select.Item
                  value={key}
                  label={meta.label}
                  isSelected={!!selectedCategories.find((cat) => cat.value === key)}
                >
                  <CategoryChip label={meta.label} color={meta.color} />
                </Select.Item>
              {/each}
            </Select.Group>
          </Select.Content>
        </div>
      </Select.Root>

      <div class="flex">
        <label class="relative inline-flex items-center cursor-pointer">
          <span class="mr-3 text-sm text-gray-900 dark:text-gray-300">
            {detailedView ? "Show Less" : "Show All"}
          </span>
          <div class="relative">
            <input type="checkbox" bind:checked={detailedView} class="sr-only peer" />
            <div
              class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500 rounded-md peer dark:bg-gray-700 peer-checked:bg-blue-600 transition-all"
            ></div>
            <div
              class="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-md transition-all peer-checked:translate-x-full"
            >
              {#if detailedView}
                <AlignJustify class="h-5 w-5 text-gray-500" />
              {:else}
                <AlignCenter class="h-5 w-5 text-gray-500" />
              {/if}
            </div>
          </div>
        </label>
      </div>
    </div>
  </div>

  <div class="overflow-y-auto">
    <ul class="flex flex-col">
      <!-- This is the "parent" folder -->
      {#if $currentPath?.length > 1}
        <li
          class={clsx(
            detailedView ? "py-4 h-20" : "py-2 h-12",
            "px-2 flex items-center justify-between space-x-4",
            "bg-gray-50",
          )}
        >
          <div class="flex space-x-3 flex-1">
            <Folder class={clsx(detailedView ? "h-7 w-7" : "h-5 w-5", "text-yellow-500")} />
            <div class="flex flex-col w-full">
              <button
                onclick={() => {
                  const parentPath = $currentPath?.slice(0, -1);
                  const params = new URLSearchParams($querystring);
                  params.set("path", parentPath?.length > 1 ? parentPath?.join("/") : "/");
                  push(`${$location}?${params.toString()}`);
                }}
                class="text-left text-blue-600 hover:underline hover:text-blue-700 font-medium transition text-base"
              >
                ..
              </button>
            </div>
          </div>
        </li>
      {/if}

      <!-- Show a loading spinner while sensitive data store is still fetching data -->
      {#if !$orgSecuritySensitiveDataStore}
        <li>
          <div class="h-full flex items-center justify-center">
            <LoadingSpinner />
          </div>
        </li>
      {/if}

      <!-- This is the list of folders -->

      {#if $orgSecuritySensitiveDataStore?.name === ROOT}
        {#each Object.entries(groupedDirectories) as [provider, rootDirs]}
          <div class="py-4">
            <div class="ml-1.5 flex gap-2 items-center">
              {#if provider.startsWith("google-drive")}
                <div class="w-6"><GoogleLogo /></div>
              {:else}
                <div class="w-6"><MicrosoftLogo /></div>
              {/if}

              <h2 class="font-semibold text-xl">
                {provider.startsWith("google-drive") ? "Google Drive" : "Microsoft Sharepoint"}
              </h2>
            </div>
            {@render directoryDisplay(rootDirs)}
          </div>
        {/each}
      {:else}
        {@render directoryDisplay(sensitiveDirectories)}
      {/if}

      {#if showNonSensitive}
        {@render directoryDisplay(nonSensitiveDirectories)}
      {/if}

      <!-- This is the list of files -->

      {#if sensitivedocuments.length > 0}
        {#each sensitivedocuments as node, index}
          <li
            class={clsx(
              detailedView ? "py-4 min-h-20" : "py-2 h-12",
              "flex items-center justify-between gap-12 px-2",
              (index + sensitiveDirectories.length + nonSensitiveDirectories.length) % 2 === 1
                ? "bg-gray-50"
                : "bg-white",
            )}
          >
            <div class="grid grid-cols-[28px_1fr_28px] auto-cols-min items-center gap-x-2">
              <File class={clsx(detailedView ? "h-7 w-7" : "h-5 w-5", "text-blue-500")} />

              <span class="text-left font-medium transition text-base self-center">
                {node.path.split("/").at(-1)}
              </span>
              <a href={node.link} target="_blank">
                <ExternalLink class="hover:scale-110 hover:text-blue-600 transition duration-100 h-4 w-4" /></a
              >
            </div>
            <div class="flex flex-wrap gap-2 justify-end max-w-[1700px]">
              {#if detailedView}
                {#each groupFindings(node.sensitiveContentCount ?? []).filter(([group]) => selectedCategories.length === 0 || selectedCategories.find((cat) => cat.value === group)) as [group, count]}
                  <CategoryChip
                    label={`${mergedCategoryMeta[group]?.label || group}: ${count}`}
                    color={mergedCategoryMeta[group]?.color}
                    title={mergedCategoryMeta[group]?.description ?? ""}
                  />
                {/each}
              {:else}
                {#each groupFindings(node.sensitiveContentCount ?? [])
                  .filter(([group]) => selectedCategories.length === 0 || selectedCategories.find((cat) => cat.value === group))
                  .slice(0, 3) as [group, count]}
                  <CategoryChip
                    class="px-2 text-xs"
                    label={`${mergedCategoryMeta[group]?.label || group}: ${count}`}
                    color={mergedCategoryMeta[group]?.color}
                    title={mergedCategoryMeta[group]?.description ?? ""}
                  />
                {/each}
              {/if}
            </div>
          </li>
        {/each}
      {/if}

      {#if showNonSensitive}
        {#each nonSensitiveDocuments as node, index}
          <li
            class={clsx(
              detailedView ? "py-4 min-h-20" : "py-2 h-12",
              "flex items-center justify-between gap-12 px-2",
              (index + sensitiveDirectories.length + nonSensitiveDirectories.length + sensitivedocuments.length) % 2 ===
                1
                ? "bg-gray-50"
                : "bg-white",
            )}
          >
            <div class="grid grid-cols-[28px_1fr_28px] auto-cols-min items-center gap-x-2">
              <File class={clsx(detailedView ? "h-7 w-7" : "h-5 w-5", "text-blue-500")} />

              <span class="text-left font-medium transition text-base self-center">
                {node.path.split("/").at(-1)}
              </span>
              <a href={node.link} target="_blank">
                <ExternalLink class="hover:scale-110 hover:text-blue-600 transition duration-100 h-4 w-4" /></a
              >
            </div>
          </li>
        {/each}
      {/if}

      {#if nonSensitiveDocuments.length + nonSensitiveDirectories.length > 0}
        {#if !showNonSensitive}
          <li class="py-4">
            <div class="flex flex-col items-center justify-center text-sm">
              <span
                >{nonSensitiveDocuments.length} non-sensitive {getDocumentText(nonSensitiveDocuments.length)} hidden</span
              >
              <span
                >{nonSensitiveDirectories.length} non-sensitive {getDirectoryText(nonSensitiveDirectories.length)} hidden</span
              >
              <button class="underline" onclick={() => (showNonSensitive = true)}>Show All</button>
            </div>
          </li>
        {:else}
          <li class="py-4">
            <div class="flex flex-col items-center justify-center text-sm">
              <button class="underline" onclick={() => (showNonSensitive = false)}>Hide Non-Sensitive</button>
            </div>
          </li>
        {/if}
      {/if}

      {#if hasNoResults}
        <div class="w-md p-4 py-8 text-center">
          <h1 class="text-lg font-bold text-gray-500">
            Indexation Paused - You cannot see these results as part of Proof of Value. <br />Contact your administrator
            to unlock all data.
          </h1>
        </div>
      {/if}
    </ul>
  </div>
</div>

{#snippet directoryDisplay(directories: DirectorySensitiveContentCount[])}
  {#each directories as node, index}
    <li
      class={clsx(
        detailedView ? "py-4 min-h-20" : "py-2 h-12",
        "flex items-center justify-between gap-12 px-2",
        index % 2 === 1 ? "bg-gray-50" : "bg-white",
      )}
    >
      <div class="grid grid-cols-[28px_1fr] grid-rows-[minmax(0,_1fr_10px)] auto-cols-min gap-x-2">
        <Folder class={clsx(detailedView ? "h-7 w-7 place-self-end" : "h-5 w-5 self-center", "text-yellow-500 ")} />
        <button
          onclick={() => {
            const params = new URLSearchParams($querystring);
            const provider = node.resourceId.split(":")[0];
            params.set("path", provider + "/" + node.path);
            push(`${$location}?${params.toString()}`);
          }}
          class={clsx(
            detailedView ? "mt-1" : "",
            "text-left text-blue-600 hover:underline hover:text-blue-700 font-medium transition text-base",
          )}
        >
          {node.path.split("/").at(-1)}
        </button>
        {#if detailedView}
          <span class="text-xs text-gray-500 text-nowrap col-start-2 row-start-2">
            {node.documentCount}
            {node.documentCount === 1 ? "document" : "documents"}
          </span>
        {/if}
      </div>
      <div class="flex flex-wrap gap-2 justify-end max-w-[1700px]">
        {#if detailedView}
          {#each groupFindings(node.sensitiveContentCount ?? []).filter(([group]) => selectedCategories.length === 0 || selectedCategories.find((cat) => cat.value === group)) as [group, count]}
            <CategoryChip
              label={`${mergedCategoryMeta[group]?.label || group}: ${count}`}
              color={mergedCategoryMeta[group]?.color}
              title={mergedCategoryMeta[group]?.description ?? ""}
            />
          {/each}
        {:else}
          {#each groupFindings(node.sensitiveContentCount ?? [])
            .filter(([group]) => selectedCategories.length === 0 || selectedCategories.find((cat) => cat.value === group))
            .slice(0, 3) as [group, count]}
            <CategoryChip
              class="px-2 text-xs"
              label={`${mergedCategoryMeta[group]?.label || group}: ${count}`}
              color={mergedCategoryMeta[group]?.color}
              title={mergedCategoryMeta[group]?.description ?? ""}
            />
          {/each}
        {/if}
      </div>
    </li>
  {/each}
{/snippet}
